/**
 * Claims List Page - View all reimbursement claims
 */

import { useState, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { reimbursementApi } from '../api/reimbursementApi'
import type { ClaimStatus } from '../types/reimbursement.types'
import LoadingPage from '@/shared/components/LoadingPage'

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All Status' },
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'paid', label: 'Paid' },
  { value: 'cancelled', label: 'Cancelled' },
]

const getStatusColor = (status: ClaimStatus) => {
  switch (status) {
    case 'draft':
      return 'bg-gray-100 text-gray-800'
    case 'submitted':
      return 'bg-blue-100 text-blue-800'
    case 'approved':
      return 'bg-green-100 text-green-800'
    case 'rejected':
      return 'bg-red-100 text-red-800'
    case 'paid':
      return 'bg-emerald-100 text-emerald-800'
    case 'cancelled':
      return 'bg-gray-100 text-gray-600'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

const formatCurrency = (amount: number, currency: string = 'IDR') => {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
  }).format(amount)
}

type SortField = 'claim_number' | 'title' | 'period' | 'total_amount' | 'status' | 'created_at'
type SortOrder = 'asc' | 'desc'

const SortIcon = ({ field, currentField, order }: { field: SortField; currentField: SortField; order: SortOrder }) => {
  if (field !== currentField) {
    return (
      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
      </svg>
    )
  }
  return order === 'asc' ? (
    <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
    </svg>
  ) : (
    <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  )
}

const ClaimsListPage = () => {
  const navigate = useNavigate()
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [page, setPage] = useState(1)
  const [sortField, setSortField] = useState<SortField>('created_at')
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')

  const { data, isLoading, error } = useQuery({
    queryKey: ['reimbursementClaims', statusFilter, page],
    queryFn: () =>
      reimbursementApi.getClaims({
        status: statusFilter || undefined,
        page,
        limit: 20,
      }),
  })

  const handleStatusChange = (newStatus: string) => {
    setStatusFilter(newStatus)
    setPage(1)
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortOrder('asc')
    }
  }

  const sortedClaims = useMemo(() => {
    const claims = data?.data || []
    return [...claims].sort((a, b) => {
      let comparison = 0
      switch (sortField) {
        case 'claim_number':
          comparison = a.claim_number.localeCompare(b.claim_number)
          break
        case 'title':
          comparison = a.title.localeCompare(b.title)
          break
        case 'period':
          const periodA = a.period_year * 100 + a.period_month
          const periodB = b.period_year * 100 + b.period_month
          comparison = periodA - periodB
          break
        case 'total_amount':
          comparison = a.total_amount - b.total_amount
          break
        case 'status':
          comparison = a.status.localeCompare(b.status)
          break
        case 'created_at':
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          break
      }
      return sortOrder === 'asc' ? comparison : -comparison
    })
  }, [data?.data, sortField, sortOrder])

  if (isLoading) return <LoadingPage />

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <p className="text-red-600">Failed to load claims</p>
        </div>
      </div>
    )
  }

  const pagination = data?.pagination

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reimbursement Claims</h1>
          <p className="text-gray-600 mt-1">Manage your expense reimbursement claims</p>
        </div>
        <Link
          to="/reimbursement/new"
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          + New Claim
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex gap-4 items-center">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Claims Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {sortedClaims.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No claims found</p>
            <Link
              to="/reimbursement/new"
              className="mt-4 inline-block px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Create your first claim
            </Link>
          </div>
        ) : (
          <>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    onClick={() => handleSort('claim_number')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center gap-1">
                      Claim #
                      <SortIcon field="claim_number" currentField={sortField} order={sortOrder} />
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('title')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center gap-1">
                      Title
                      <SortIcon field="title" currentField={sortField} order={sortOrder} />
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('period')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center gap-1">
                      Period
                      <SortIcon field="period" currentField={sortField} order={sortOrder} />
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('total_amount')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center gap-1">
                      Amount
                      <SortIcon field="total_amount" currentField={sortField} order={sortOrder} />
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('status')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center gap-1">
                      Status
                      <SortIcon field="status" currentField={sortField} order={sortOrder} />
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('created_at')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center gap-1">
                      Created
                      <SortIcon field="created_at" currentField={sortField} order={sortOrder} />
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedClaims.map((claim) => (
                  <tr key={claim.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/reimbursement/claims/${claim.id}`)}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link
                        to={`/reimbursement/claims/${claim.id}`}
                        className="text-sm font-medium text-red-600 hover:text-red-800 hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {claim.claim_number}
                      </Link>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{claim.title}</div>
                      {claim.item_count !== undefined && (
                        <div className="text-xs text-gray-500">
                          {claim.item_count} item{claim.item_count !== 1 ? 's' : ''}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {format(new Date(claim.period_year, claim.period_month - 1), 'MMM yyyy')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(claim.total_amount, claim.currency)}
                      </div>
                      {claim.original_amount !== claim.total_amount && (
                        <div className="text-xs text-gray-500 line-through">
                          {formatCurrency(claim.original_amount, claim.currency)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${getStatusColor(claim.status)}`}
                      >
                        {claim.status.replace('_', ' ')}
                      </span>
                      {claim.has_policy_violations && (
                        <span className="ml-2 text-xs text-orange-600" title="Has policy violations">
                          ⚠
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {format(new Date(claim.created_at), 'MMM dd, yyyy')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {pagination && pagination.pages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Page {pagination.page} of {pagination.pages} ({pagination.total} total)
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page >= pagination.pages}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default ClaimsListPage
